import { z } from 'zod';

import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * The client-side map pack — geometry, and nothing else (M5-D11).
 *
 * > The node graph (`map.regions`, `map.ranges`) is the model and the only thing
 * > on the wire. Geometry, terrain and vector display are a **client-side asset
 * > keyed by scenario name**.
 *
 * A pack entry is a **selector, not a polygon**. `vendor/maps/region-map.js`
 * resolves `sources[].land` against `world-atlas`' `countries-50m` and
 * `sources[].sea` against Natural Earth's marine polygons, both keyed by
 * `properties.name`, then filters the resolved rings by `bbox` (ring centroid
 * inside the window) and cuts them to `clip` (Sutherland–Hodgman against a
 * declared lon/lat extent). So a pack is a few hundred bytes of *lookup*, and
 * the geometry itself is the atlas the bundle already carries.
 *
 * ## What an invented region looks like on real geography
 *
 * First Contact's regions are AURELIA, BOREA, CALDIS and DORNE — names that
 * carry `adjacentTo` and nothing else. Three rules decide what they are drawn
 * on, and they are rules rather than a taste:
 *
 * 1. **Terrain, not a polity.** A zone is cut out of real ground with a declared
 *    `clip` box, so its boundary is a straight exercise line and never an
 *    international border. Nothing on the canvas can be read as a claim about a
 *    real state, which is the failure mode of renaming four countries.
 * 2. **Chosen for emptiness and for shape.** Northern Australia is sparsely
 *    populated, carries no interstate dispute a player could import, and is one
 *    landmass wide enough to hold four zones of comparable size in a line — so
 *    the drawn adjacency agrees with `map.regions[].adjacentTo` without being
 *    asked to: AURELIA–BOREA–CALDIS–DORNE touch in that order and no other pair
 *    touches at all.
 * 3. **The pack names no place.** Only the scenario's own names are drawn.
 *    `sources[].land` is a lookup key and never reaches the screen.
 *
 * ## `site`
 *
 * One `[lon, lat]` per region, on that region's own ground. It exists because
 * `mw-map.js` projects launch origins and impact points as coordinates and
 * `MwTrackView` carries `RegionId`s (`model/mw-map-records.ts`). It is a display
 * anchor: **no distance is ever computed from it**, and `map.ranges` is the only
 * thing that answers a range question anywhere in this console.
 */

const zLonLat = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

/** `[west, south, east, north]`, degrees. */
const zBox = z.tuple([z.number(), z.number(), z.number(), z.number()]);

export const zMapSource = z
  .object({
    /** A `properties.name` in world-atlas `countries-50m`. */
    land: z.string().optional(),
    /** A `properties.name` in the Natural Earth marine polygons. See `maps/vendor.ts`. */
    sea: z.string().optional(),
    /** Keep only the source's rings whose centroid falls inside this window. */
    bbox: zBox.optional(),
    /** Cut the source's rings to this declared operational extent. */
    clip: zBox.optional(),
  })
  .strict()
  .refine((source) => (source.land === undefined) !== (source.sea === undefined), {
    message: 'A source is one of land or sea, never both and never neither',
  });

export const zMapPackRegion = z
  .object({
    id: z.string(),
    site: zLonLat,
    sources: z.array(zMapSource).min(1),
  })
  .strict();

export const zMapPack = z
  .object({
    /** The scenario this pack is for, so a mis-slugged filename fails loudly. */
    scenario: z.string(),
    /** Free text for whoever opens the file next. Never rendered. */
    note: z.string().optional(),
    regions: z.array(zMapPackRegion).min(1),
  })
  .strict();

export type MapPack = z.infer<typeof zMapPack>;
export type MapPackRegion = z.infer<typeof zMapPackRegion>;

/**
 * Region ids the view declares that the pack cannot place.
 *
 * The plan asks for exactly this assertion — *"a renamed region fails loudly
 * rather than dropping a marker in the sea"* — and the renderer is not enough on
 * its own: `buildRings` `console.warn`s a missing source and carries on with an
 * empty ring list, which draws a theatre that is simply missing a quarter of
 * itself with nothing on screen to say so.
 *
 * So coverage is checked *before* the element is handed a model, the answer is
 * the list of ids rather than a boolean, and `theatre/TheatreView.tsx` names
 * them instead of drawing a map.
 */
export function packCoverage(view: CellViewWire, pack: MapPack): readonly string[] {
  const placed = new Set(pack.regions.map((region) => region.id));
  return view.map.regions.filter((region) => !placed.has(region.id)).map((region) => region.id);
}

/** Region id → `[lon, lat]`, the argument `mwMapRecords` has been waiting for. */
export function packGeometry(pack: MapPack): Readonly<Record<string, readonly [number, number]>> {
  return Object.fromEntries(pack.regions.map((region) => [region.id, region.site]));
}
